// src/Dashboard/pages/user/Galleries/components/PhotoLightbox.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaExternalLinkAlt,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl as toAbsPhoto } from "../../../../../services/albumPhotoServices";

import {
  FALLBACK_IMAGE,
  onImgError,
  stripHtml,
} from "../utils/galleriesUtils";

export default function PhotoLightbox({ photos, startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [purify, setPurify] = useState(null);

  const current = photos[index] || {};
  const currentSrc = toAbsPhoto(current?.photo_url || "");

  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {}
    })();
  }, []);

  const safeDesc = useMemo(() => {
    const raw = current?.description || "";

    try {
      return purify ? purify.sanitize(raw) : raw;
    } catch {
      return raw;
    }
  }, [current?.description, purify]);

  const prev = useCallback(() => {
    setIndex((value) => (value - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const next = useCallback(() => {
    setIndex((value) => (value + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowLeft") prev();
      if (event.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, prev, next]);

  useEffect(() => {
    const preload = (photoIndex) => {
      const photo = photos[photoIndex];

      if (!photo?.photo_url) return;

      const img = new Image();
      img.src = toAbsPhoto(photo.photo_url);
    };

    if (photos.length > 1) {
      preload((index + 1) % photos.length);
      preload((index - 1 + photos.length) % photos.length);
    }
  }, [index, photos]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between p-4 text-white">
        <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-bold ring-1 ring-white/15">
          {index + 1} / {photos.length}
        </span>

        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
          title="Fermer"
        >
          <FaTimes />
        </button>
      </div>

      <div className="w-[min(1100px,95vw)] max-w-[95vw]">
        <img
          src={currentSrc || FALLBACK_IMAGE}
          alt={stripHtml(current?.description || `Photo ${index + 1}`)}
          className="max-h-[78vh] w-full rounded-2xl object-contain shadow-2xl"
          onError={onImgError}
        />

        {(current?.description || currentSrc) && (
          <div className="mt-3 rounded-2xl bg-white/10 p-4 text-white ring-1 ring-white/10">
            {current?.description ? (
              <div
                className="prose prose-sm max-w-none text-white prose-invert [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                dangerouslySetInnerHTML={{ __html: safeDesc }}
              />
            ) : (
              <div className="break-all text-xs opacity-75">{currentSrc}</div>
            )}
          </div>
        )}
      </div>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="absolute left-4 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 md:left-6"
            title="Photo précédente"
          >
            <FaChevronLeft />
          </button>

          <button
            type="button"
            onClick={next}
            className="absolute right-4 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 md:right-6"
            title="Photo suivante"
          >
            <FaChevronRight />
          </button>
        </>
      )}

      {currentSrc && (
        <div className="absolute bottom-4 right-4 flex gap-2">
          <a
            href={currentSrc}
            target="_blank"
            rel="noopener noreferrer"
            title="Ouvrir l’original"
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
          >
            <FaExternalLinkAlt />
          </a>

          <a
            href={currentSrc}
            download
            title="Télécharger"
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
          >
            <FaDownload />
          </a>
        </div>
      )}
    </div>,
    document.body
  );
}